import React, { useState } from 'react';
import {
  Menu,
  MenuItem,
  Box,
  Typography,
  Avatar,
  IconButton,
  Divider,
  CircularProgress,
  Button,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { CONTEXTS } from 'figshelf-modules';
import { useNavigate } from 'react-router-dom';

const { useNotifications } = CONTEXTS;

const NotificationsMenu = ({ anchorEl, open, onClose }) => {
  const navigate = useNavigate();
  const { notifications, loading, unreadCount, markAllAsRead } = useNotifications();

  const handleNotificationClick = (notification) => {
    // Mark as read if unread
    if (!notification.isRead && notification.id) {
      markAllAsRead(notification.id);
    }

    const { type, metadata } = notification;
    
    console.log('Notification clicked:', type, 'metadata:', metadata);

    try {
      // Navigate based on notification type
      switch (type) {
        // Legacy types (kept for backward compatibility)
        case 'FOLLOW':
          if (notification.sender?.username) {
            navigate(`/user/${notification.sender.username}`);
          } else if (metadata?.userId) {
            navigate(`/user/id/${metadata.userId}`);
          }
          break;
        case 'LIKE':
        case 'COMMENT':
          if (notification.post?.id) {
            navigate(`/post/${notification.post.id}`);
          } else if (metadata?.postId) {
            navigate(`/post/${metadata.postId}`);
          }
          break;
        case 'MESSAGE':
          if (metadata?.conversationId) {
            navigate(`/messages/${metadata.conversationId}`);
          } else {
            navigate('/messages');
          }
          break;

        // Figure-related notifications
        case 'figure_approved':
        case 'FIGURE_APPROVED':
          if (metadata?.figureId) {
            navigate(`/figure/${metadata.figureId}`);
          }
          break;
        
        case 'figure_rejected':
        case 'figure_submitted':
        case 'FIGURE_REJECTED':
        case 'FIGURE_SUBMITTED':
          // Navigate to pending figures or profile
          navigate('/profile');
          break;

        // Figure Database Entry notifications
        case 'figure_database_approved':
        case 'figure_database_rejected':
        case 'figure_database_submitted':
        case 'FIGURE_DATABASE_APPROVED':
        case 'FIGURE_DATABASE_REJECTED':
        case 'FIGURE_DATABASE_SUBMITTED':
          if (metadata?.entryId) {
            navigate(`/database/${metadata.entryId}`);
          }
          break;

        // Company notifications
        case 'company_approved':
        case 'COMPANY_APPROVED':
          if (metadata?.companyId) {
            navigate(`/company/${metadata.companyId}`);
          }
          break;
        
        case 'company_rejected':
        case 'company_submitted':
        case 'COMPANY_REJECTED':
        case 'COMPANY_SUBMITTED':
          // Navigate to profile or pending companies
          navigate('/profile');
          break;

        // Group notifications
        case 'group_created':
        case 'group_invitation':
        case 'group_member_joined':
        case 'GROUP_CREATED':
        case 'GROUP_INVITATION':
        case 'GROUP_MEMBER_JOINED':
          if (metadata?.groupId || notification.groupId) {
            navigate(`/group/${metadata?.groupId || notification.groupId}`);
          }
          break;

        // Follow notifications
        case 'new_follower':
        case 'NEW_FOLLOWER':
          if (notification.sender?.username) {
            navigate(`/user/${notification.sender.username}`);
          } else if (notification.sender?.id) {
            navigate(`/user/id/${notification.sender.id}`);
          } else if (metadata?.userId) {
            navigate(`/user/id/${metadata.userId}`);
          }
          break;

        // Post notifications (like, comment, share)
        case 'post_liked':
        case 'post_commented':
        case 'post_shared':
        case 'POST_LIKED':
        case 'POST_COMMENTED':
        case 'POST_SHARED':
          if (metadata?.postId) {
            navigate(`/post/${metadata.postId}`);
          } else if (notification.post?.id) {
            navigate(`/post/${notification.post.id}`);
          }
          break;

        // Message notifications
        case 'new_message':
        case 'NEW_MESSAGE':
          if (metadata?.conversationId) {
            navigate(`/messages/${metadata.conversationId}`);
          } else {
            navigate('/messages');
          }
          break;

        // Marketplace notifications
        case 'marketplace_listing_approved':
        case 'marketplace_listing_rejected':
        case 'MARKETPLACE_LISTING_APPROVED':
        case 'MARKETPLACE_LISTING_REJECTED':
          if (metadata?.listingId) {
            navigate(`/marketplace/${metadata.listingId}`);
          } else {
            navigate('/marketplace');
          }
          break;

        // Info/System notifications (scheduled posts, reminders, etc.)
        case 'info':
        case 'INFO':
          if (metadata?.postId) {
            navigate(`/post/${metadata.postId}`);
          } else if (metadata?.figureId) {
            navigate(`/figure/${metadata.figureId}`);
          } else if (metadata?.groupId) {
            navigate(`/group/${metadata.groupId}`);
          } else if (metadata?.companyId) {
            navigate(`/company/${metadata.companyId}`);
          } else if (metadata?.conversationId) {
            navigate(`/messages/${metadata.conversationId}`);
          }
          break;

        case 'premium_activated':
        case 'PREMIUM_ACTIVATED':
          navigate('/settings/subscription');
          break;

        default:
          // Try to navigate based on metadata if type is unknown
          if (metadata?.postId) {
            navigate(`/post/${metadata.postId}`);
          } else if (metadata?.figureId) {
            navigate(`/figure/${metadata.figureId}`);
          } else if (metadata?.groupId) {
            navigate(`/group/${metadata.groupId}`);
          } else if (metadata?.companyId) {
            navigate(`/company/${metadata.companyId}`);
          } else {
            console.log('No navigation handler for notification type:', type);
          }
          break;
      }
    } catch (error) {
      console.error('Error navigating from notification:', error);
    }

    onClose();
  };

  const handleMarkAllRead = () => {
    if (notifications.length > 0 && notifications[0].id) {
      markAllAsRead(notifications[0].id);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'FOLLOW':
        return '👤';
      case 'LIKE':
        return '❤️';
      case 'COMMENT':
        return '💬';
      case 'MESSAGE':
        return '✉️';
      case 'premium_activated':
      case 'PREMIUM_ACTIVATED':
        return '★';
      default:
        return '🔔';
    }
  };

  const formatTime = (date) => {
    if (!date) return '';
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch (error) {
      return '';
    }
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 10001,
      }}
      PaperProps={{
        elevation: 3,
        sx: {
          width: 360,
          maxHeight: 480,
          overflow: 'visible',
          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
          mt: 1.5,
          borderRadius: 2,
          zIndex: 10001,
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Notifications
        </Typography>
        {unreadCount > 0 && (
          <Button
            size="small"
            onClick={handleMarkAllRead}
            sx={{ textTransform: 'none', fontSize: '0.875rem' }}
          >
            Mark all read
          </Button>
        )}
      </Box>

      {/* Notifications List */}
      <Box sx={{ maxHeight: 480, overflowY: 'auto', overflowX: 'hidden', backgroundColor: 'background.paper' }}>
        {loading && notifications.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>
            <Typography variant="body2">No notifications yet</Typography>
          </Box>
        ) : (
          notifications.map((notification, index) => (
            <React.Fragment key={notification.id || index}>
              <MenuItem
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  px: 2,
                  py: 1.5,
                  backgroundColor: notification.isRead ? 'transparent' : 'rgba(73, 160, 173, 0.08)',
                  '&:hover': {
                    backgroundColor: notification.isRead ? 'rgba(0, 0, 0, 0.04)' : 'rgba(73, 160, 173, 0.12)',
                  },
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                }}
              >
                {/* Avatar or Icon */}
                {notification.sender?.profile?.currentAvatar?.url ? (
                  <Avatar
                    src={notification.sender.profile.currentAvatar.url}
                    sx={{ width: 40, height: 40 }}
                  >
                    {notification.sender.username?.[0]?.toUpperCase()}
                  </Avatar>
                ) : (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                    }}
                  >
                    {getNotificationIcon(notification.type)}
                  </Box>
                )}

                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: notification.isRead ? 400 : 600,
                      color: 'text.primary',
                      mb: 0.5,
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal',
                    }}
                  >
                    {notification.message || notification.body || 'New notification'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {formatTime(notification.createdAt)}
                  </Typography>
                </Box>

                {/* Unread Indicator */}
                {!notification.isRead && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      mt: 0.5,
                    }}
                  />
                )}
              </MenuItem>
              {index < notifications.length - 1 && <Divider />}
            </React.Fragment>
          ))
        )}
      </Box>

      {/* Footer */}
      {notifications.length > 0 && (
        <>
          <Divider />
          <Box sx={{ p: 1, textAlign: 'center', backgroundColor: 'background.paper' }}>
            <Button
              fullWidth
              onClick={() => {
                navigate('/notifications');
                onClose();
              }}
              sx={{ textTransform: 'none' }}
            >
              View all notifications
            </Button>
          </Box>
        </>
      )}
    </Menu>
  );
};

export default NotificationsMenu;
